import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import {
  Wallet,
  TrendingUp,
  CreditCard,
  RefreshCw,
  Percent,
  ArrowRightLeft,
  CircleDollarSign,
  Save,
  Pencil,
  X,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCard, formatCents } from "./shared";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { usePlatformStats } from "@/hooks/use-queries";

const FLOW_TYPE_LABELS: Record<string, string> = {
  wallet_contribution: "Wallet Contributions",
  wallet_subscription: "Recurring Support",
  community_fund_contribution: "Community Funds",
  condition_wallet_contribution: "Condition Wallets",
  emergency_care_contribution: "Emergency Care",
  precare_subscription: "Pre-Care Subscriptions",
  payment_schedule: "Payment Schedules",
};

export function FinancialDashboard({ wallets, subscriptions }: { wallets: any[]; subscriptions: any[] }) {
  const { data: platformStats, isLoading: statsLoading } = usePlatformStats();

  // Use pre-aggregated DB totals from platform-stats (accurate, single SQL SUM query).
  // Fallback to client-side reduction if stats haven't loaded yet.
  const totalBalance = platformStats?.wallets?.totalBalance
    ?? wallets.reduce((sum, w) => sum + (w.balance || 0), 0);
  const totalContributions = platformStats?.wallets?.totalContributions
    ?? wallets.reduce((sum, w) => sum + (w.totalContributions || 0), 0);
  const totalSpent = wallets.reduce((sum, w) => sum + (w.totalSpent || 0), 0);

  const activeSubscriptions = subscriptions?.filter((s: any) => s.status === "active").length || 0;
  const monthlyRecurring = subscriptions?.filter((s: any) => s.status === "active")
    .reduce((sum: number, s: any) => sum + (s.amountCents || 0), 0) || 0;

  const { data: feeSummary, isLoading: feeSummaryLoading } = useQuery<any>({
    queryKey: ["/api/admin/platform-fees/summary"],
  });

  const { data: recentFees, isLoading: recentFeesLoading } = useQuery<any[]>({
    queryKey: ["/api/admin/platform-fees/recent"],
  });

  return (
    <>
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          title="Total Wallet Balance"
          value={statsLoading ? "—" : formatCents(totalBalance)}
          description={`Across ${platformStats?.wallets?.count ?? wallets.length} patient wallets`}
          icon={Wallet}
          color="green"
        />
        <StatCard
          title="Total Contributed"
          value={statsLoading ? "—" : formatCents(totalContributions)}
          description="All-time contributions"
          icon={TrendingUp}
          color="blue"
        />
        <StatCard
          title="Total Spent"
          value={formatCents(totalSpent)}
          description="Applied to healthcare costs"
          icon={CreditCard}
          color="purple"
        />
        <StatCard
          title="Active Subscriptions"
          value={activeSubscriptions}
          description={`${formatCents(monthlyRecurring)}/month recurring`}
          icon={RefreshCw}
          color="primary"
        />
      </div>

      <Card data-testid="platform-revenue-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CircleDollarSign className="w-5 h-5" />
            Platform Revenue
          </CardTitle>
          <CardDescription>Service fees collected across all payment flows</CardDescription>
        </CardHeader>
        <CardContent>
          {feeSummaryLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : feeSummary ? (
            <div className="space-y-6">
              <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
                <div className="p-4 rounded-lg border" data-testid="revenue-total-fees">
                  <Label className="text-xs text-muted-foreground">Total Fees Collected</Label>
                  <p className="text-xl font-bold text-green-600">
                    {formatCents(Number(feeSummary.totals?.totalFees) || 0)}
                  </p>
                </div>
                <div className="p-4 rounded-lg border" data-testid="revenue-total-gross">
                  <Label className="text-xs text-muted-foreground">Gross Payments Processed</Label>
                  <p className="text-xl font-bold">
                    {formatCents(Number(feeSummary.totals?.totalGross) || 0)}
                  </p>
                </div>
                <div className="p-4 rounded-lg border" data-testid="revenue-total-net">
                  <Label className="text-xs text-muted-foreground">Net to Recipients</Label>
                  <p className="text-xl font-bold text-blue-600">
                    {formatCents(Number(feeSummary.totals?.totalNet) || 0)}
                  </p>
                </div>
                <div className="p-4 rounded-lg border" data-testid="revenue-transaction-count">
                  <Label className="text-xs text-muted-foreground">Transactions</Label>
                  <p className="text-xl font-bold">
                    {Number(feeSummary.totals?.transactionCount) || 0}
                  </p>
                </div>
              </div>

              {feeSummary.byFlowType && feeSummary.byFlowType.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-medium mb-3">Revenue by Payment Flow</h4>
                    <div className="space-y-2">
                      {feeSummary.byFlowType.map((flow: any) => (
                        <div
                          key={flow.paymentFlowType}
                          className="flex items-center justify-between gap-2 p-3 rounded-lg bg-muted/50"
                          data-testid={`revenue-flow-${flow.paymentFlowType}`}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium">
                              {FLOW_TYPE_LABELS[flow.paymentFlowType] || flow.paymentFlowType}
                            </span>
                            <Badge variant="secondary" className="text-xs">
                              {Number(flow.transactionCount)} txns
                            </Badge>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-bold text-green-600">
                              {formatCents(Number(flow.totalFees))}
                            </span>
                            <span className="text-xs text-muted-foreground ml-2">
                              ({Number(flow.avgFeePercentage).toFixed(1)}%)
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No platform fee data available yet</p>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <FeeScheduleEditor rates={feeSummary?.rates} isLoading={feeSummaryLoading} />

        <Card data-testid="recent-fees-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5" />
              Recent Fee Transactions
            </CardTitle>
            <CardDescription>Latest platform fee collections</CardDescription>
          </CardHeader>
          <CardContent>
            {recentFeesLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : recentFees && recentFees.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {recentFees.slice(0, 10).map((fee: any) => (
                  <div
                    key={fee.id}
                    className="flex items-center justify-between gap-2 p-3 rounded-lg bg-muted/50"
                    data-testid={`fee-entry-${fee.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {FLOW_TYPE_LABELS[fee.paymentFlowType] || fee.paymentFlowType}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {fee.contributorName || "Anonymous"} &middot; {new Date(fee.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-green-600">+{formatCents(fee.feeAmount)}</p>
                      <p className="text-xs text-muted-foreground">of {formatCents(fee.grossAmount)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No fee transactions yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card data-testid="subscription-status-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              Subscription Status
            </CardTitle>
            <CardDescription>Subscription tiers are configured via Stripe</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(() => {
                const statusCounts: Record<string, number> = {};
                subscriptions.forEach((s: any) => {
                  const status = s.status || "unknown";
                  statusCounts[status] = (statusCounts[status] || 0) + 1;
                });
                const entries = Object.entries(statusCounts);
                if (entries.length === 0) {
                  return <p className="text-sm text-muted-foreground text-center py-4">No subscriptions found</p>;
                }
                return entries.map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between gap-2 p-3 rounded-lg bg-muted/50" data-testid={`subscription-status-${status}`}>
                    <div className="flex items-center gap-2">
                      <Badge variant={status === "active" ? "default" : status === "past_due" ? "destructive" : "secondary"}>
                        {status}
                      </Badge>
                    </div>
                    <span className="font-medium">{count}</span>
                  </div>
                ));
              })()}
              <Separator />
              <div className="flex items-center justify-between gap-2 p-2">
                <span className="text-sm font-medium">Total Subscriptions</span>
                <span className="font-bold">{subscriptions.length}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="platform-policy-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Platform Policy
            </CardTitle>
            <CardDescription>HealthWallet platform constants</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
              <div className="p-4 rounded-lg border" data-testid="policy-interest-rate">
                <Label className="text-xs text-muted-foreground">Interest Rate</Label>
                <p className="text-xl font-bold text-green-600">0%</p>
              </div>
              <div className="p-4 rounded-lg border" data-testid="policy-max-duration">
                <Label className="text-xs text-muted-foreground">Max Duration</Label>
                <p className="text-xl font-bold">60 months</p>
              </div>
              <div className="p-4 rounded-lg border" data-testid="policy-hardship-pause">
                <Label className="text-xs text-muted-foreground">Hardship Pause Limit</Label>
                <p className="text-xl font-bold">90 days</p>
              </div>
              <div className="p-4 rounded-lg border" data-testid="policy-auto-debit">
                <Label className="text-xs text-muted-foreground">Auto-Debit Enabled</Label>
                <p className="text-xl font-bold text-green-600">Yes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function FeeScheduleEditor({ rates, isLoading }: { rates: Record<string, number> | undefined; isLoading: boolean }) {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editedRates, setEditedRates] = useState<Record<string, number>>({});

  const updateMutation = useMutation({
    mutationFn: async (ratesPayload: { flowType: string; feePercentage: number }[]) => {
      const res = await apiRequest("PUT", "/api/admin/platform-fees/rates", { rates: ratesPayload });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/platform-fees/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/platform-fees/rates"] });
      setIsEditing(false);
      setEditedRates({});
      toast({ title: "Fee rates updated", description: "New rates will apply to future transactions." });
    },
    onError: (error: any) => {
      toast({ title: "Update failed", description: error.message || "Could not update fee rates.", variant: "destructive" });
    },
  });

  function startEditing() {
    if (rates) {
      setEditedRates({ ...rates });
    }
    setIsEditing(true);
  }

  function cancelEditing() {
    setIsEditing(false);
    setEditedRates({});
  }

  function handleRateChange(flowType: string, value: string) {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      setEditedRates(prev => ({ ...prev, [flowType]: num }));
    }
  }

  function handleSave() {
    const ratesPayload = Object.entries(editedRates).map(([flowType, feePercentage]) => ({
      flowType,
      feePercentage,
    }));

    for (const rate of ratesPayload) {
      if (rate.feePercentage < 0 || rate.feePercentage > 50) {
        toast({ title: "Invalid rate", description: `Fee for ${FLOW_TYPE_LABELS[rate.flowType] || rate.flowType} must be between 0% and 50%.`, variant: "destructive" });
        return;
      }
    }

    updateMutation.mutate(ratesPayload);
  }

  const hasChanges = rates && Object.entries(editedRates).some(
    ([key, val]) => val !== rates[key]
  );

  return (
    <Card data-testid="fee-schedule-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Percent className="w-5 h-5" />
              Fee Schedule
            </CardTitle>
            <CardDescription>Platform service fee rates by payment flow</CardDescription>
          </div>
          {!isEditing ? (
            <Button
              variant="outline"
              size="sm"
              onClick={startEditing}
              data-testid="button-edit-fees"
            >
              <Pencil className="w-4 h-4 mr-1" />
              Edit Rates
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={cancelEditing}
                data-testid="button-cancel-edit-fees"
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!hasChanges || updateMutation.isPending}
                data-testid="button-save-fees"
              >
                <Save className="w-4 h-4 mr-1" />
                {updateMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : (
          <div className="space-y-2">
            {rates ? (
              Object.entries(rates).map(([flowType, rate]) => (
                <div
                  key={flowType}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/50"
                  data-testid={`fee-rate-${flowType}`}
                >
                  <span className="text-sm">{FLOW_TYPE_LABELS[flowType] || flowType}</span>
                  {isEditing ? (
                    <div className="flex items-center gap-1 shrink-0">
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="50"
                        value={editedRates[flowType] ?? rate}
                        onChange={(e) => handleRateChange(flowType, e.target.value)}
                        className="w-20 text-right"
                        data-testid={`input-fee-rate-${flowType}`}
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  ) : (
                    <Badge variant="outline">{Number(rate)}%</Badge>
                  )}
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">Loading fee rates...</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
